import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Updater } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';

import {
  ST,
  AppointmentDetailDTO,
  CallOutcomeEnum,
  Location,
  ServiceTitanApiPhoneCallCreateSchema,
  ServiceTypeEnum,
  SERVICE_TYPES,
  SupportedProviderEnum,
  isEmailOutcome,
  isEmployeeAssignOutcome,
  isNewAppointmentOutcome,
  isPreviousAppointmentOutcome,
  isRequiredNotesOutcome,
  LocationSchema,
  TradeEnum,
  ServiceTitanApiPhoneCallCreateDTO,
  Paths,
  isRequiredCustomerOutcome,
  isRequiredJobOutcome,
  ServiceTitanMappedCall,
  transformAssignedEmployee,
  isRequiredServiceTypeOutcome,
  isRequiredTradeOutcome
} from '_lib/api/apiTypes.shim';

import { useQueryClient } from '_lib/api/QueryProvider';
import { CompleteCallWrapper } from '_lib/callPop/common/CompleteCallWrapper/CompleteCallWrapper';
import { useCallLogNavigate } from '_lib/callPop/useCallLogNavigate';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { Box } from '_lib/components/Box/Box';
import { Button } from '_lib/components/Button/Button';
import { Card } from '_lib/components/Card/Card';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { DebouncedTextField } from '_lib/components/DebouncedTextField/DebouncedTextField';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { ExternalLinkOrDefault } from '_lib/components/ExternalLinkOrDefault/ExternalLinkOrDefault';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { DatePicker } from '_lib/components/Pickers/DatePicker/DatePicker';
import { Stack } from '_lib/components/Stack/Stack';
import { Switch } from '_lib/components/Switch/Switch';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';

import { CompanyLabels } from '_lib/constants/company.constants';
import { CallDetailsFooter } from '_lib/featureComponents/CallDetailsFooter/CallDetailsFooter';
import { CallOutcomeSelect } from '_lib/featureComponents/CallOutcomeSelect/CallOutcomeSelect';
import { CancelReasonSelect } from '_lib/featureComponents/CancelReasonSelect/CancelReasonSelect';
import { CardBox } from '_lib/featureComponents/CardBox/CardBox';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { LocationSelect } from '_lib/featureComponents/Location/LocationSelect';
import { ServiceTypeSelect } from '_lib/featureComponents/ServiceType/ServiceTypeSelect';
import { TechAssignCard } from '_lib/featureComponents/TechAssignCard/TechAssignCard';
import { TradeSelect } from '_lib/featureComponents/Trades/TradeSelect';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { useLocationFormDialog } from '_lib/hooks/LocationFormDialogHook';
import { useTechAssignDialog } from '_lib/hooks/TechAssignDialogHook';
import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useAppSelector, useAppDispatch } from '_lib/redux/hooks';
import { addAppListener } from '_lib/redux/listenerMiddleware';
import { CustomerContext, PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { DateUtils } from '_lib/utils/date.utils';

import { getDefaultTrade, getOnCallNotes, isLocationEqual, toSxArray } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { ClearIssueItem } from '_lib/utils/validationErrorUtils';

import { zipZoneDialogCheck } from '_lib/utils/zipZoneDialogUtils';

import {
  isNewZoneSelectedPredicate,
  isStApiCompanyContext,
  selectZoneZipContext,
  StApiCompanyContext,
  ZoneZipContext
} from '../../../../common/CompanyPane/Company.slice';
import {
  HeldAppointmentContext,
  HeldAppointment,
  setAppointmentBookingPaneOpen,
  setPartialPhoneCallContext,
  setHeldAppointmentContext,
  selectAppointmentBookingPaneOpen,
  selectHeldAppointmentContext,
  selectCustomerContext,
  isCustomerChangePredicate,
  isHeldAppointmentChangePredicate,
  setCustomerContextLocations,
  setPhoneCallNonFieldErrors,
  selectPhoneCallContext,
  PhoneCallContext,
  selectPhoneCallFieldIssues,
  selectPhoneCallNonFieldErrors,
  setPhoneCallFieldIssues,
  initialize
} from '../../StApiPhoneCall.slice';

import { BookingState } from '../AppointmentBookingPane/AppointmentBookingPane.types';

import { AppointmentCard } from './AppointmentCard/AppointmentCard';
import { RequestedAppointment } from './RequestedAppointment/RequestedAppointment';
import { StApiPreviousJobSelect } from './StApiPreviousJobSelect/StApiPreviousJobSelect';

const serviceTitanApiPhoneCallCreateSchema = (company: StApiCompanyContext['company']) =>
  ServiceTitanApiPhoneCallCreateSchema.transform((obj, ctx) => transformAssignedEmployee(obj, ctx, company));

interface CallDetailsPaneProps {
  phoneCallURLState: PhoneCallURLState;
  companyContext: StApiCompanyContext;
  mappedCall: ServiceTitanMappedCall | null;
  aiCallId?: string;
  bookingState: BookingState;
  setBookingState: Updater<BookingState>;
  sx?: SxProps<Theme>;
  showFooter?: boolean;
  onSetIsSaving: (isSaving: boolean) => void;
}

// TODO: rethink this!
export const handledIssues: Pick<
  { [k in Paths<ServiceTitanApiPhoneCallCreateDTO>]: k },
  | 'appointment'
  | 'appointmentDate'
  | 'previousAppointmentDate'
  | 'notes'
  | 'internalNotes'
  | 'outcome'
  | 'serviceType'
  | 'trade'
  | 'providerData.providerJobId'
  | 'providerData.providerCancelReasonId'
  | 'providerData.providerLocationId'
  | 'providerData.providerAssignedEmployee'
> = {
  appointment: 'appointment',
  appointmentDate: 'appointmentDate',
  previousAppointmentDate: 'previousAppointmentDate',
  notes: 'notes',
  internalNotes: 'internalNotes',
  outcome: 'outcome',
  serviceType: 'serviceType',
  trade: 'trade',
  'providerData.providerJobId': 'providerData.providerJobId',
  'providerData.providerCancelReasonId': 'providerData.providerCancelReasonId',
  'providerData.providerLocationId': 'providerData.providerLocationId',
  'providerData.providerAssignedEmployee': 'providerData.providerAssignedEmployee'
} as const;

/**
 * @returns if user can Request an Appointment given the state of the Call Outcome
 */
const canRequestAppointmentForOutcome = (outcome?: CallOutcomeEnum): boolean => {
  if (outcome && [CallOutcomeEnum.Cancellation, CallOutcomeEnum.UpdateNotes].includes(outcome)) return false;
  return true;
};

/**
 * @returns if user can Request an Appointment given the state of the CompanyContext and ZoneZipContext
 */
const canRequestAppointmentForCompany = (options: {
  companyCtx?: StApiCompanyContext;
  zoneZipContext?: ZoneZipContext;
}): boolean => {
  const { companyCtx, zoneZipContext } = options;
  if (!companyCtx?.company.isScheduleEnabled) return false;
  return companyCtx.company.isZoned ? !!zoneZipContext?.selectedZone : !!zoneZipContext?.selectedZipOrPostal;
};

/**
 * @returns if user can Request an Appointment given the state of the CompanyContext, ZoneZipContext, and selected call outcome
 */
const canRequestAppointment = (options: {
  companyCtx?: StApiCompanyContext;
  zoneZipContext?: ZoneZipContext;
  outcome?: CallOutcomeEnum;
}): boolean => {
  const { outcome, ...rest } = options;
  return canRequestAppointmentForCompany(rest) && canRequestAppointmentForOutcome(outcome);
};

const isPreviousAppointmentRequired = <
  T extends Pick<PhoneCallContext, 'callOutcome' | 'overridePreviousAppointmentDate'>
>(
  callContext: T
) => isPreviousAppointmentOutcome(callContext.callOutcome) && !callContext.overridePreviousAppointmentDate;

/**
 * @returns if there is a heldAppointment that will be ignored and released for the current outcome
 */
const heldAppointmentForWrongOutcome = (phoneCallContext: PhoneCallContext, callDetailsCtx: HeldAppointmentContext) =>
  !!(
    callDetailsCtx.heldAppointment &&
    phoneCallContext.callOutcome &&
    !isNewAppointmentOutcome(phoneCallContext.callOutcome)
  );

/**
 * @returns Component for recording information and completing call
 */
export const CallDetailsPane = (props: CallDetailsPaneProps): React.ReactElement => {
  const {
    phoneCallURLState,
    companyContext,
    mappedCall,
    aiCallId,
    bookingState,
    setBookingState,
    sx,
    showFooter,
    onSetIsSaving
  } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = api.serviceTitan(companyContext.company.id);

  const queryClient = useQueryClient();

  const { navigateToCallLog } = useCallLogNavigate();
  const { enqueueSnackbar } = useOurSnackbar();
  const authRoles = useAuthRoles();

  const locationFormDialog = useLocationFormDialog();
  const techAssignDialog = useTechAssignDialog();
  const confirmationDialog = useConfirmationDialog();
  const alertDialog = useAlertDialog();

  const [notifyTechRequired, setNotifyTechRequired] = useState(false);
  const [techNotified, setTechNotified] = useState(false);

  /* -------- REDUX -------- */

  const zoneZipContext = useAppSelector(selectZoneZipContext);
  const appointmentBookingPaneOpen = useAppSelector(selectAppointmentBookingPaneOpen);
  const heldAppointmentContext = useAppSelector(selectHeldAppointmentContext);
  const customerContext = useAppSelector(selectCustomerContext);
  const phoneCallContext = useAppSelector(selectPhoneCallContext);
  const fieldIssues = useAppSelector(selectPhoneCallFieldIssues);
  const nonFieldErrors = useAppSelector(selectPhoneCallNonFieldErrors);

  const dispatch = useAppDispatch();

  /* -------- END REDUX -------- */

  const handlePhoneCallContextUpdate = useCallback(
    (updates: Partial<PhoneCallContext>, clearIssues?: ClearIssueItem[]) => {
      dispatch(setPartialPhoneCallContext({ updates, clearIssues }));
    },
    [dispatch]
  );

  const handleHardDeleteAppointment = useCallback(
    (heldAppointment: HeldAppointment, options?: { reason?: string; companyContext?: StApiCompanyContext }) => {
      dispatch(setPhoneCallNonFieldErrors([]));

      // Intentionally not using await as API task will cleanup appointments if the delete fails.
      api.appointments.hardDelete(heldAppointment.appointment.id).catch((err) => {
        reportException(err, {
          extra: {
            message: `Appointment hard delete failed ${options?.reason || ''}`,
            heldAppointment,
            companyContext: options?.companyContext
          }
        });
      });
      enqueueSnackbar(`Appointment deleted. ${options?.reason || ''}`);

      // clear out the appointment
      dispatch(setHeldAppointmentContext({ heldAppointment: undefined }));
    },
    [api.appointments, dispatch, enqueueSnackbar]
  );

  const handleNotifyTechInfoClicked = async () => {
    const contactScheduleUrl =
      (companyContext.company.isZoned && zoneZipContext?.selectedZone?.contactScheduleUrl) ||
      companyContext.company.contactScheduleUrl;

    await alertDialog.show({
      renderMarkup: true,
      dialogMaxWidth: '600px',
      contentTextWhiteSpace: 'pre-line',
      titleText: 'On Call Instructions',
      content: [
        <>
          <HeaderBox headerText='Contact Schedule URL'>
            <ExternalLinkOrDefault url={contactScheduleUrl} label={CompanyLabels.CONTACT_SCHEDULE_URL} />
          </HeaderBox>
        </>,
        getOnCallNotes(companyContext.company, zoneZipContext?.selectedZone)
      ]
    });
  };

  const setLocationIfValid = useCallback(
    async (location: Location | undefined, customerLocations: NonNullable<CustomerContext['locations']>) => {
      const result = LocationSchema.safeParse(location);
      if (location && !result.success) {
        const result = await locationFormDialog.show({
          location,
          locationResource: serviceTitanApi.locations
        });
        if (!result || result.action === 'CANCEL') return;
        else {
          const locationResult = result.value;
          const updatedLocations = customerLocations.map((l) =>
            l.providerId !== location.providerId ? l : locationResult
          );

          dispatch(setCustomerContextLocations(updatedLocations));
          handlePhoneCallContextUpdate(
            { previousServiceTitanAppointment: undefined, previousAppointment: undefined, location: locationResult },
            [handledIssues['providerData.providerLocationId']]
          );
        }
      } else {
        handlePhoneCallContextUpdate(
          { previousServiceTitanAppointment: undefined, previousAppointment: undefined, location },
          location ? [handledIssues['providerData.providerLocationId']] : undefined
        );
      }
    },
    [dispatch, handlePhoneCallContextUpdate, locationFormDialog, serviceTitanApi.locations]
  );

  useEffect(() => {
    setNotifyTechRequired(
      isNewAppointmentOutcome(phoneCallContext.callOutcome) &&
        !!heldAppointmentContext.heldAppointment?.companyWindow?.isOnCall
    );
  }, [heldAppointmentContext.heldAppointment, phoneCallContext.callOutcome]);

  useEffect(() => {
    // - Customer Location List Change Handler ST API - //
    // listens for changes to customer locations to see if the Selected Location has been updated, so that we can then
    // update the selected location along with any other behavior (zone/zip change effects, etc..)
    return dispatch(
      addAppListener({
        actionCreator: setCustomerContextLocations,
        effect: ({ payload: updatedLocations }, api) => {
          logDebug('// - Customer Location List Change Handler ST API - //');
          const phoneCallContext = api.getState().stApiPhoneCallSlice.phoneCallContext;
          if (phoneCallContext.location?.providerId) {
            const maybeUpdatedLocation = updatedLocations.find(
              (location) => location.providerId === phoneCallContext.location?.providerId
            );
            if (maybeUpdatedLocation && !isLocationEqual(maybeUpdatedLocation, phoneCallContext.location)) {
              handlePhoneCallContextUpdate({ location: maybeUpdatedLocation });
            } else if (!maybeUpdatedLocation) {
              handlePhoneCallContextUpdate({ location: undefined });
            }
          }
        }
      })
    );
  }, [dispatch, handlePhoneCallContextUpdate]);

  useEffect(() => {
    // - Customer Change Handler ST API - //
    return dispatch(
      addAppListener({
        predicate: isCustomerChangePredicate,
        effect: async ({ payload: customerCtx }) => {
          // When changing customer, we always reset the following PhoneCallState:
          logDebug('// - Customer Change Handler ST API - //');

          // Every time a customer change happens, we blow away selected appointment/job and location
          handlePhoneCallContextUpdate({
            previousServiceTitanAppointment: undefined,
            previousAppointment: undefined,
            location: undefined
          });

          // Try to auto set location if only one exists
          const customerLocations = customerCtx?.locations;

          if (customerLocations && customerLocations?.length === 1) {
            const location = customerLocations[0];
            await setLocationIfValid(location, customerLocations);
          }
        }
      })
    );
  }, [companyContext, dispatch, handlePhoneCallContextUpdate, setLocationIfValid]);

  useEffect(() => {
    // - New Zone Selected Handler ST API - //
    // if new zone is selected and the current heldAppointment is not for the new zone, we clear out the appointment
    return dispatch(
      addAppListener({
        predicate: isNewZoneSelectedPredicate,
        effect: ({ payload: zoneZipContext }, api) => {
          logDebug('// - New Zone Selected Handler ST API - //');
          if (!zoneZipContext?.selectedZone) return; // Should be false (see predicate). For ease of TS we'll keep the guard

          // TODO - discuss difficulty with managing zone:
          // - it's a nonFieldError
          // - if it was a fieldIssue, we should be handling/displaying it here
          if (nonFieldErrors?.length) {
            const updatedErrors = nonFieldErrors.filter((msg) => !msg.toLowerCase().includes('zone'));
            if (updatedErrors.length !== nonFieldErrors.length) dispatch(setPhoneCallNonFieldErrors(updatedErrors));
          }

          const heldAppointment = api.getState().stApiPhoneCallSlice.heldAppointmentContext.heldAppointment;
          const heldAppointmentZones = heldAppointment?.companyWindow.zones || [];
          if (heldAppointment && !heldAppointmentZones.includes(zoneZipContext.selectedZone.id)) {
            handleHardDeleteAppointment(heldAppointment, { reason: 'Zone changed' });
          }
        }
      })
    );
  }, [dispatch, handleHardDeleteAppointment, nonFieldErrors]);

  useEffect(() => {
    // - heldAppointmentContext.appointmentHeld ChangeHandler ST API - //
    return dispatch(
      addAppListener({
        predicate: isHeldAppointmentChangePredicate,
        effect: ({ payload: callDetailsCtx }) => {
          logDebug('// - heldAppointmentContext.appointmentHeld ChangeHandler ST API - //');

          if (callDetailsCtx.heldAppointment) {
            enqueueSnackbar('Appointment held for call!');
            const heldAppointment = callDetailsCtx.heldAppointment;

            const partialChanges: Partial<PhoneCallContext> = {
              appointmentTime: heldAppointment.appointment.date,
              appointmentDateTime: heldAppointment.appointment.date
            };

            if (heldAppointment.companyWindow.serviceTypes.length === 1) {
              partialChanges.serviceType = heldAppointment.companyWindow.serviceTypes[0];
            } else if (
              heldAppointment.serviceTypeFromFilter &&
              heldAppointment.companyWindow.serviceTypes.includes(heldAppointment.serviceTypeFromFilter)
            ) {
              partialChanges.serviceType = heldAppointment.serviceTypeFromFilter;
            } else if (
              phoneCallContext.serviceType &&
              !heldAppointment.companyWindow.serviceTypes.includes(phoneCallContext.serviceType)
            ) {
              partialChanges.serviceType = undefined;
            }

            if (companyContext.company.callCenter.trades.length === 1) {
              partialChanges.trade = companyContext.company.callCenter.trades[0];
            } else if (heldAppointment.companyWindow.trades.length === 1) {
              partialChanges.trade = heldAppointment.companyWindow.trades[0];
            } else if (
              heldAppointment.tradeFromFilter &&
              heldAppointment.companyWindow.trades.includes(heldAppointment.tradeFromFilter)
            ) {
              partialChanges.trade = heldAppointment.tradeFromFilter;
            } else if (
              phoneCallContext.trade &&
              !heldAppointment.companyWindow.trades.includes(phoneCallContext.trade)
            ) {
              partialChanges.trade = undefined;
            }

            handlePhoneCallContextUpdate(partialChanges, [
              handledIssues.appointmentDate,
              { issuePath: handledIssues.appointment, matchPartial: true },
              ...(partialChanges.serviceType ? [handledIssues.serviceType] : []),
              ...(partialChanges.trade ? [handledIssues.trade] : [])
            ]);
          } else {
            const defaultTrade = getDefaultTrade(companyContext.company);
            handlePhoneCallContextUpdate(
              { trade: defaultTrade, appointmentTime: undefined, appointmentDateTime: undefined },
              defaultTrade ? [handledIssues.trade] : []
            );
          }
        }
      })
    );
  }, [
    dispatch,
    enqueueSnackbar,
    phoneCallContext.serviceType,
    phoneCallContext.trade,
    companyContext,
    handlePhoneCallContextUpdate,
    fieldIssues
  ]);

  useEffect(() => {
    // - Listener to determine if we should show Requested Appointment - //
    return dispatch(
      addAppListener({
        predicate: (_action, { companySlice }, { companySlice: prevCompanySlice }) =>
          companySlice.companyContext?.company.id !== prevCompanySlice.companyContext?.company.id ||
          companySlice.zoneZipContext?.selectedZone?.id !== prevCompanySlice.zoneZipContext?.selectedZone?.id ||
          companySlice.zoneZipContext?.selectedZipOrPostal !== prevCompanySlice.zoneZipContext?.selectedZipOrPostal,
        effect: (_action, api) => {
          logDebug('// - Listener to determine if we should show Requested Appointment - //');
          const newCompanySlice = api.getState().companySlice;
          handlePhoneCallContextUpdate({
            showRequestedAppointment:
              isStApiCompanyContext(newCompanySlice.companyContext) &&
              canRequestAppointment({
                companyCtx: newCompanySlice.companyContext,
                outcome: phoneCallContext.callOutcome,
                zoneZipContext: newCompanySlice.zoneZipContext
              })
          });
        }
      })
    );
  }, [dispatch, handlePhoneCallContextUpdate, phoneCallContext.callOutcome]);

  type MultiAppointmentJobStateOverrides = {
    callOutcome?: CallOutcomeEnum;
    providerJobId?: string;
  };

  /**
   * @param overrides - by default, values of the current PhoneCallState are investigated. Pass in an override to
   * explicitly check a potentially new PhoneCallState value.
   * @returns whether or not the PhoneCallState has providerJobId set such that there is more than one appointment for
   * the job AND the callOutcome is either: Cancellation or Reschedule
   */
  const invalidStateMultiAppointmentJob = async (overrides: MultiAppointmentJobStateOverrides = {}) => {
    const callOutcome = overrides?.callOutcome ? overrides.callOutcome : phoneCallContext?.callOutcome;
    const providerJobId = overrides?.providerJobId
      ? overrides.providerJobId
      : phoneCallContext?.previousServiceTitanAppointment?.providerJobId;

    if (!customerContext || !providerJobId || !callOutcome || !isPreviousAppointmentOutcome(callOutcome)) return false;

    const customerScheduledAppointmentResource = serviceTitanApi.customers.scheduledAppointments(
      customerContext.customer.providerId
    );

    const serviceTitanAppointments = await queryClient.ensureQueryData({
      queryKey: customerScheduledAppointmentResource.cachedQueryKey(companyContext.company.timezone),
      queryFn: () => customerScheduledAppointmentResource.unpagedListCached(companyContext.company.timezone)
    });

    const possibleDupes = serviceTitanAppointments?.filter(
      (serviceTitanAppointment) => serviceTitanAppointment.providerJobId === providerJobId
    );

    return !!possibleDupes && possibleDupes.length > 1;
  };

  /**
   * Displays a warning dialog if the agent selected a job with multiple appointments (Cancel and Reschedule Only)
   */
  const openMultiAppointmentJobWarning = async () => {
    await alertDialog.show({
      variant: 'warning',
      titleText: 'CANNOT MODIFY MULTI-DAY JOBS',
      content:
        'You are attempting to Cancel or Reschedule a multi-day job. This is not currently supported by our system.'
    });
  };

  const handleSetCallOutcome = async (callOutcome: CallOutcomeEnum) => {
    // prevent updating state if we detect an issue with new outcome
    if (await invalidStateMultiAppointmentJob({ callOutcome })) {
      await openMultiAppointmentJobWarning();
      return;
    }

    const phoneCallContextUpdates: Partial<PhoneCallContext> = {
      callOutcome,
      showRequestedAppointment: canRequestAppointment({
        companyCtx: companyContext,
        outcome: callOutcome,
        zoneZipContext
      })
    };

    if (isEmailOutcome(callOutcome)) phoneCallContextUpdates.sendCompanyEmail = true;

    if ([CallOutcomeEnum.Cancellation, CallOutcomeEnum.UpdateNotes].includes(callOutcome)) {
      phoneCallContextUpdates.appointmentDateTime = undefined;
    }

    // reset any error state based on setting outcome:
    const clearIssues: ClearIssueItem[] = [handledIssues.outcome];

    if (!isNewAppointmentOutcome(callOutcome)) {
      clearIssues.push({ issuePath: handledIssues.appointment, matchPartial: true });
      clearIssues.push(handledIssues.appointmentDate);
    }

    if (!isPreviousAppointmentRequired({ ...phoneCallContext, callOutcome })) {
      clearIssues.push(handledIssues.previousAppointmentDate);
    }

    if (callOutcome !== CallOutcomeEnum.Cancellation) {
      clearIssues.push(handledIssues['providerData.providerCancelReasonId']);
    }

    if (!isEmployeeAssignOutcome(callOutcome)) {
      clearIssues.push({ issuePath: handledIssues['providerData.providerAssignedEmployee'], matchPartial: true });
    }

    if (!isRequiredCustomerOutcome(callOutcome)) clearIssues.push(handledIssues['providerData.providerLocationId']);
    if (!isRequiredJobOutcome(callOutcome)) clearIssues.push(handledIssues['providerData.providerJobId']);
    if (!isRequiredNotesOutcome(callOutcome)) clearIssues.push(handledIssues.notes);

    handlePhoneCallContextUpdate(phoneCallContextUpdates, clearIssues);
  };

  const handleSetLocation = async (locationProviderId: string) => {
    const location = customerContext?.locations.find((location) => location.providerId === locationProviderId);
    if (location?.providerId !== phoneCallContext.location?.providerId && customerContext?.locations)
      await setLocationIfValid(location, customerContext.locations);
  };

  /**
   * "Fallback" for when we do not have an appointment in our calendar for a previous appointment
   */
  const handleSetPreviousAppointmentDate = (value: Date | null) => {
    handlePhoneCallContextUpdate(
      { previousAppointmentDate: value && value.toISOString() },
      value ? [handledIssues.previousAppointmentDate] : undefined
    );
  };

  const handleSetOverridePreviousAppointmentDate = (overridePreviousAppointmentDate: boolean) => {
    handlePhoneCallContextUpdate(
      { overridePreviousAppointmentDate },
      overridePreviousAppointmentDate ? [handledIssues.previousAppointmentDate] : undefined
    );
  };

  const handleSetOverrideEmployeeId = (overrideEmployeeId: boolean) => {
    handlePhoneCallContextUpdate(
      { overrideEmployeeId },
      overrideEmployeeId ? [handledIssues['providerData.providerAssignedEmployee']] : undefined
    );
  };

  const handleClearAssignedTech = useCallback(() => {
    handlePhoneCallContextUpdate({ assignedTech: undefined });
  }, [handlePhoneCallContextUpdate]);

  const handleOpenTechAssignDialog = async () => {
    const { company } = companyContext;
    const promise = await techAssignDialog.show({
      companyId: company.id,
      onCallText: getOnCallNotes(company, zoneZipContext?.selectedZone),
      contactScheduleUrl:
        company.isZoned && zoneZipContext?.selectedZone?.contactScheduleUrl
          ? zoneZipContext?.selectedZone.contactScheduleUrl
          : company.contactScheduleUrl
    });

    if (promise?.action === 'CONFIRM') {
      handlePhoneCallContextUpdate({ assignedTech: promise.value }, [
        { issuePath: handledIssues['providerData.providerAssignedEmployee'], matchPartial: true }
      ]);
    }
  };

  const handleAppointmentDateTimeChange = (value: Date | null) => {
    handlePhoneCallContextUpdate(
      { appointmentDateTime: value ? value.toISOString() : null },
      value ? [handledIssues.appointmentDate] : undefined
    );
  };

  const handleOpenAppointmentBookingPane = () => {
    if (bookingState.filterContext) {
      const tradeFilterKey = bookingState.filterContext.tradeFilter.selectedKey;
      if (phoneCallContext.trade && phoneCallContext.trade !== tradeFilterKey) {
        setBookingState((state) => {
          state.forceFilterRefresh = true;
          if (state.filterContext && phoneCallContext.trade)
            state.filterContext.tradeFilter.selectedKey = phoneCallContext.trade;
        });
      } else if (!phoneCallContext.trade && tradeFilterKey) {
        handlePhoneCallContextUpdate({ trade: tradeFilterKey }, [handledIssues.trade]);
      }

      const serviceTypeFilterKey = bookingState.filterContext.serviceTypeFilter.selectedKey;
      if (phoneCallContext.serviceType && phoneCallContext.serviceType !== serviceTypeFilterKey) {
        setBookingState((state) => {
          state.forceFilterRefresh = true;
          if (state.filterContext && phoneCallContext.serviceType)
            state.filterContext.serviceTypeFilter.selectedKey = phoneCallContext.serviceType;
        });
      } else if (!phoneCallContext.serviceType && serviceTypeFilterKey) {
        handlePhoneCallContextUpdate({ serviceType: serviceTypeFilterKey }, [handledIssues.serviceType]);
      }
    }
    dispatch(setAppointmentBookingPaneOpen(true));
  };

  const handleSetRequestedTimeMinutes = (value: Date) => {
    handlePhoneCallContextUpdate({ appointmentTime: value.toISOString() }, [handledIssues.appointmentDate]);
  };

  const handleSetServiceType = (value: ServiceTypeEnum) => {
    handlePhoneCallContextUpdate({ serviceType: value }, [handledIssues.serviceType]);
  };

  /**
   * Used to prevent passing invalid serviceType to serviceType select when we hold an appointment
   */
  const getServiceType = () => {
    if (
      phoneCallContext.serviceType &&
      heldAppointmentContext.heldAppointment &&
      !heldAppointmentContext.heldAppointment.companyWindow.serviceTypes.includes(phoneCallContext.serviceType)
    )
      return null;
    return phoneCallContext.serviceType;
  };

  const handleSetTrade = (value: TradeEnum) => {
    handlePhoneCallContextUpdate({ trade: value }, [handledIssues.trade]);
  };

  /**
   * Used to prevent passing invalid trade to trade select when we hold an appointment
   */
  const getTrade = () => {
    if (
      phoneCallContext.trade &&
      heldAppointmentContext.heldAppointment &&
      !heldAppointmentContext.heldAppointment.companyWindow.trades.includes(phoneCallContext.trade)
    ) {
      return null;
    }

    return phoneCallContext.trade;
  };

  const handleSetCancelReason = (value?: ST.JobCancelReason) => {
    handlePhoneCallContextUpdate(
      { cancelReason: value },
      value ? [handledIssues['providerData.providerCancelReasonId']] : undefined
    );
  };

  const handleAppointmentCardOnClose = useCallback(async () => {
    const promise = await confirmationDialog.show({
      titleText: 'DELETE APPOINTMENT',
      contentText: 'Do you want to delete this appointment?'
    });

    if (promise?.action === 'CONFIRM' && heldAppointmentContext.heldAppointment) {
      handleHardDeleteAppointment(heldAppointmentContext.heldAppointment);
      if (phoneCallContext.assignedTech) handleClearAssignedTech();
    }
  }, [
    confirmationDialog,
    heldAppointmentContext.heldAppointment,
    handleHardDeleteAppointment,
    phoneCallContext.assignedTech,
    handleClearAssignedTech
  ]);

  const completeCall = async (data: ServiceTitanApiPhoneCallCreateDTO) => {
    try {
      onSetIsSaving(true);

      await api.serviceTitanApiPhoneCalls.create(data);

      navigateToCallLog();
    } catch (err) {
      // If we somehow pass validation client side and fail on the api, just dump it (for now)
      if (err instanceof BadRequestError) {
        dispatch(setPhoneCallNonFieldErrors(err.responseData.nonFieldErrors));
        dispatch(
          setPhoneCallFieldIssues(
            err.responseData.fieldIssues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors') || []
          )
        );
      }
      reportException(err);
    } finally {
      onSetIsSaving(false);
    }
  };

  const handleCompleteCall = async () => {
    dispatch(setPhoneCallFieldIssues([]));
    dispatch(setPhoneCallNonFieldErrors([]));

    // Mainly for sanity check and ease of TS below. If any of these are wrong, nothing else is worth running
    if (!phoneCallContext.callOutcome) {
      enqueueSnackbar('Call Outcome is Required');
      return;
    }

    if (await invalidStateMultiAppointmentJob()) {
      openMultiAppointmentJobWarning();
      return;
    }

    let appointmentDate: string | undefined = undefined;
    // TS won't let me mutate the appointment date unless I do this
    const appointment: AppointmentDetailDTO | undefined = heldAppointmentContext.heldAppointment?.appointment
      ? { ...heldAppointmentContext.heldAppointment.appointment }
      : undefined;

    // Merge heldAppointmentContext.heldAppointment.appointment.date  with phoneCallContext.appointmentTime
    // (heldAppointmentContext.appointmentDateTime if heldAppointment is not set?)
    const appointmentTime = phoneCallContext.appointmentTime;

    if (appointment && appointmentTime) {
      appointmentDate = DateUtils.mergeDateAndTime(
        appointment.date,
        appointmentTime,
        companyContext.company.timezone
      ).toISOString();
      appointment.date = appointmentDate;
    } else if (phoneCallContext.appointmentDateTime && appointmentTime) {
      // TODO - this is an unreachable statement!
      appointmentDate = DateUtils.mergeDateAndTime(
        phoneCallContext.appointmentDateTime,
        appointmentTime,
        companyContext.company.timezone
      ).toISOString();
    } else if (phoneCallContext.appointmentDateTime) {
      appointmentDate = phoneCallContext.appointmentDateTime;
    }

    const company = companyContext.company;
    const companyZone = zoneZipContext?.selectedZone;

    const providerMappedCall =
      company.dataProvider.providerOptions.tenantId === companyContext.companyProviderData?.originalCompanyTenantId
        ? mappedCall
        : null;

    const result = serviceTitanApiPhoneCallCreateSchema(company).safeParse({
      id: phoneCallURLState.phoneCallId,
      providerType: SupportedProviderEnum.SERVICE_TITAN_API,
      companyId: company.id,
      callerId: phoneCallURLState.callerId,
      companyDNIS: phoneCallURLState.companyDNIS,
      companyIsZoned: company.isZoned,
      companySwitched: companyContext.switched,
      duration: Date.now() - phoneCallURLState.durationStart,
      isManualCall: phoneCallURLState.isManualCall,
      outcome: phoneCallContext.callOutcome,
      sendCompanyEmail: phoneCallContext.sendCompanyEmail,
      sendEscalationEmail: phoneCallContext.sendEscalationEmail,
      companyZoneId: companyZone?.id || null,
      companyZoneTitle: companyZone?.title || null,
      internalNotes: phoneCallContext.internalNotes || null,
      notes: phoneCallContext.notes || null,
      originalCompany: companyContext.originalCompanyName || null,
      trade: phoneCallContext.trade || null,
      appointment: appointment || null,
      appointmentDate: appointmentDate || null,
      previousAppointment: phoneCallContext?.previousAppointment || null,
      previousAppointmentDate:
        phoneCallContext.previousAppointmentDate &&
        DateUtils.toDateString(phoneCallContext.previousAppointmentDate, { timezone: companyContext.company.timezone }),
      serviceType: phoneCallContext.serviceType || null,
      providerData: {
        providerCustomerId: customerContext?.customer.providerId,
        providerLocationId: phoneCallContext.location?.providerId,
        providerJobId: phoneCallContext?.previousServiceTitanAppointment?.providerJobId,
        providerAppointmentId: phoneCallContext?.previousServiceTitanAppointment?.providerId,
        providerCancelReasonId: phoneCallContext?.cancelReason?.id?.toString(),
        providerAssignedEmployee: phoneCallContext?.assignedTech
          ? {
              email: phoneCallContext.assignedTech.email,
              firstName: phoneCallContext.assignedTech.firstName,
              lastName: phoneCallContext.assignedTech.lastName,
              phoneNumber: phoneCallContext.assignedTech.phoneNumber,
              providerId: phoneCallContext.assignedTech.providerId
            }
          : undefined,
        providerMappedCall
      },
      aiCallId: aiCallId || null,
      telephonyId: phoneCallURLState.telephonyId || null,
      _fieldOverrides: {
        previousAppointmentDate: phoneCallContext.overridePreviousAppointmentDate,
        employeeId: phoneCallContext.overrideEmployeeId
      }
    });

    if (result.success && result.data) {
      if (!company.isZoned) return completeCall(result.data);

      await zipZoneDialogCheck({
        companyContext,
        selectedZone: zoneZipContext?.selectedZone || null,
        zipOrPostal: phoneCallContext.location?.zipOrPostal,
        confirmationCallback: () => completeCall(result.data),
        confirmationDialog,
        alertDialog
      });
    } else if (!result.success) {
      dispatch(setPhoneCallFieldIssues(result.error.issues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors')));

      // TODO - Discuss with Dave O. and better "filter" the issues, ie: one's handled explicitly by
      //        `setFieldIssues` & "extras" here
      //
      // NOTE: this also ensures we display any custom path field issues, ie:
      //       for phone call, there are a few cases where a field issue may have path: ['nonFieldErrors']
      const handledIssueValues = Object.values(handledIssues);
      const extraErrors: string[] = result.error.issues
        .filter(({ path }) => !handledIssueValues.find((issue) => issue === path.join('.')))
        .map(({ message }) => message);

      if (extraErrors.length) dispatch(setPhoneCallNonFieldErrors(extraErrors));

      enqueueSnackbar('Phone Call is Invalid!');
    }
  };

  /**
   * Programmatically revert to 'initial' state
   */
  const reInitCallDetailsPane = async () => {
    if (heldAppointmentContext.heldAppointment) {
      // TODO - we can improve re init to keep more state (IE: a held appointment is quite important)
      // NOTE - doing so may be unwise - this is only an option on catastrophic non-caught exceptions
      //        and is meant as a hard reset of any state that could have triggered it
      api.appointments.hardDelete(heldAppointmentContext.heldAppointment.appointment.id).catch((err) => {
        reportException(err, {
          extra: {
            message: 'Appointment hard delete failed when re-initializing state during call pop',
            heldAppointment: heldAppointmentContext.heldAppointment,
            companyContext
          }
        });
      });
    }
    dispatch(initialize({ company: companyContext.company, options: { keepExistingCallData: true } }));
  };

  const showCompleteCallWarning = heldAppointmentForWrongOutcome(phoneCallContext, heldAppointmentContext);

  return (
    <Box
      sx={[
        (theme) => ({
          bgcolor: theme.palette.callPane.main,
          color: theme.palette.callPane.contrastText,
          display: 'flex',
          flexDirection: 'column'
        }),
        ...toSxArray(sx)
      ]}>
      <ErrorBoundary
        beforeCapture={(scope) => {
          scope.setTag('pane', 'Call Details Pane - Inner');
        }}
        fallback={({ resetError }) => (
          <ErrorBoundaryFallback
            title='Error Displaying Call Details'
            onRefresh={() => {
              reInitCallDetailsPane();
              resetError();
            }}
          />
        )}>
        <Stack
          direction='row'
          sx={{ justifyContent: 'space-between', alignItems: 'center', paddingX: 1.5, paddingY: 2 }}>
          <Typography variant='h4' text='CALL DETAILS' />
        </Stack>
        {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1, margin: 1 }} />}
        <Stack sx={{ paddingX: 1.5, paddingBottom: 1.5, overflow: 'auto', position: 'relative', flexGrow: 1 }}>
          <CardBox>
            <CallOutcomeSelect
              callOutcome={phoneCallContext?.callOutcome}
              issuePath={handledIssues.outcome}
              issues={fieldIssues}
              onSetCallOutcome={handleSetCallOutcome}
            />
          </CardBox>
          <Tooltip
            title={
              appointmentBookingPaneOpen ? 'Location selection is disabled when an appointment is being scheduled.' : ''
            }
            followCursor={true}>
            <span>
              <CardBox>
                <LocationSelect
                  onSetLocation={handleSetLocation}
                  location={phoneCallContext.location}
                  locations={customerContext?.locations}
                  disabled={!customerContext || appointmentBookingPaneOpen}
                  required={isRequiredCustomerOutcome(phoneCallContext.callOutcome)}
                  issuePath={handledIssues['providerData.providerLocationId']}
                  issues={fieldIssues}
                />
              </CardBox>
            </span>
          </Tooltip>
          {phoneCallContext?.callOutcome === CallOutcomeEnum.Cancellation && customerContext && (
            <CardBox>
              <CancelReasonSelect
                companyId={companyContext.company.id}
                cancelReason={phoneCallContext?.cancelReason}
                onSetCancelReason={handleSetCancelReason}
                issuePath={handledIssues['providerData.providerCancelReasonId']}
                issues={fieldIssues}
              />
            </CardBox>
          )}
          {phoneCallContext.callOutcome !== CallOutcomeEnum.NewAppointment && (
            <>
              <CardBox>
                <StApiPreviousJobSelect
                  issuePath={handledIssues['providerData.providerJobId']}
                  issues={fieldIssues}
                  companyId={companyContext.company.id}
                  timezone={companyContext.company.timezone}
                  providerCustomerId={customerContext?.customer.providerId}
                  providerLocationId={phoneCallContext?.location?.providerId}
                  required={isRequiredJobOutcome(phoneCallContext.callOutcome)}
                  onSetServiceTitanAppointment={async ({
                    previousServiceTitanAppointment,
                    previousCalendarAppointment
                  }) => {
                    if (
                      await invalidStateMultiAppointmentJob({
                        providerJobId: previousServiceTitanAppointment.providerJobId
                      })
                    ) {
                      openMultiAppointmentJobWarning();
                    } else {
                      const previousAppointmentDate = previousServiceTitanAppointment.start;
                      handlePhoneCallContextUpdate(
                        {
                          previousServiceTitanAppointment,
                          previousAppointment: previousCalendarAppointment,
                          previousAppointmentDate
                        },
                        [handledIssues['providerData.providerJobId'], handledIssues.previousAppointmentDate]
                      );
                    }
                  }}
                  previousServiceTitanAppointment={phoneCallContext.previousServiceTitanAppointment}
                  onClearPreviousServiceTitanAppointment={() => {
                    // If we are clearing job, also clear Calendar Appointment
                    handlePhoneCallContextUpdate({
                      previousServiceTitanAppointment: undefined,
                      previousAppointment: undefined
                    });
                  }}
                />
              </CardBox>
              {phoneCallContext?.previousAppointment?.id && (
                <AppointmentCard
                  appointment={phoneCallContext.previousAppointment}
                  appointmentDisplayType='Previous'
                  timezone={companyContext.company.timezone}
                  isManualEmployeeAssignEnabled={companyContext.company.isManualEmployeeAssignEnabled}
                  dismissable={false}
                />
              )}
              {!phoneCallContext?.previousAppointment?.id &&
                (!isRequiredJobOutcome(phoneCallContext.callOutcome) ||
                  (isRequiredJobOutcome(phoneCallContext.callOutcome) &&
                    phoneCallContext?.previousServiceTitanAppointment)) && (
                  <CardBox>
                    <DatePicker
                      clearable
                      label='Previous Appointment Date'
                      value={
                        phoneCallContext.previousAppointmentDate
                          ? new Date(phoneCallContext.previousAppointmentDate)
                          : null
                      }
                      textFieldProps={{
                        error: {
                          issues: fieldIssues,
                          issuePath: handledIssues.previousAppointmentDate
                        },
                        required: isPreviousAppointmentRequired(phoneCallContext)
                      }}
                      timezone={companyContext.company.timezone}
                      onChange={handleSetPreviousAppointmentDate}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color='primary'
                          checked={phoneCallContext.overridePreviousAppointmentDate}
                          onChange={(e) => {
                            if (!e.target.checked) handleSetOverridePreviousAppointmentDate(false);
                            else handleSetOverridePreviousAppointmentDate(true);
                          }}
                        />
                      }
                      label={<Typography text='Override Field Requirements' />}
                    />
                  </CardBox>
                )}
            </>
          )}
          {!heldAppointmentContext.heldAppointment && phoneCallContext.showRequestedAppointment && (
            <RequestedAppointment
              onDateSelectChange={handleAppointmentDateTimeChange}
              onOpenAppointmentWindows={handleOpenAppointmentBookingPane}
              appointmentDateTime={phoneCallContext.appointmentDateTime}
              appointmentDateTimeFieldIssueProps={{ issues: fieldIssues, issuePath: handledIssues.appointmentDate }}
              timezone={companyContext.company.timezone}
              required={isNewAppointmentOutcome(phoneCallContext.callOutcome)}
            />
          )}
          {heldAppointmentContext.heldAppointment && (
            <AppointmentCard
              appointmentDisplayType='Held'
              onCloseAppointment={handleAppointmentCardOnClose}
              onSetRequestedTimeMinutes={handleSetRequestedTimeMinutes}
              appointment={heldAppointmentContext.heldAppointment.appointment}
              appointmentTimeOverride={
                phoneCallContext.appointmentTime ? new Date(phoneCallContext.appointmentTime) : null
              }
              timezone={companyContext.company.timezone}
              isManualEmployeeAssignEnabled={companyContext.company.isManualEmployeeAssignEnabled}
              requestedTimeFieldIssueProps={{ issuePath: handledIssues.appointmentDate, issues: fieldIssues }}
            />
          )}
          {isRequiredTradeOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <TradeSelect
                trade={getTrade()}
                onSetTrade={handleSetTrade}
                trades={
                  heldAppointmentContext.heldAppointment
                    ? heldAppointmentContext.heldAppointment.companyWindow.trades
                    : companyContext.company.callCenter.trades
                }
                issuePath={handledIssues.trade}
                issues={fieldIssues}
                disabled={appointmentBookingPaneOpen}
              />
            </CardBox>
          )}
          {isRequiredServiceTypeOutcome(phoneCallContext.callOutcome) && (
            <CardBox>
              <ServiceTypeSelect
                serviceType={getServiceType()}
                onSetServiceType={handleSetServiceType}
                serviceTypes={
                  heldAppointmentContext.heldAppointment
                    ? heldAppointmentContext.heldAppointment.companyWindow.serviceTypes
                    : SERVICE_TYPES
                }
                issuePath={handledIssues.serviceType}
                issues={fieldIssues}
                disabled={appointmentBookingPaneOpen}
              />
            </CardBox>
          )}
          {phoneCallContext.callOutcome === CallOutcomeEnum.NewAppointment &&
            companyContext.company.isManualEmployeeAssignEnabled &&
            heldAppointmentContext?.heldAppointment?.companyWindow.isTechAssign && (
              <TechAssignCard
                onClearAssignedTech={handleClearAssignedTech}
                assignedTech={phoneCallContext?.assignedTech}
                issues={fieldIssues}
                issuePath={handledIssues['providerData.providerAssignedEmployee']}
                matchPartial={true}
                onSetOverrideEmployeeId={handleSetOverrideEmployeeId}
                onOpenTechAssign={handleOpenTechAssignDialog}
                overrideEmployeeId={phoneCallContext.overrideEmployeeId}
              />
            )}
          <CardBox>
            <DebouncedTextField
              fullWidth
              label='Notes'
              required={isRequiredNotesOutcome(phoneCallContext.callOutcome)}
              multiline
              error={{
                issuePath: handledIssues.notes,
                issues: fieldIssues
              }}
              debounceOptions={{ initialValue: phoneCallContext.notes }}
              onChangeHandler={(notes: string) => {
                handlePhoneCallContextUpdate({ notes }, notes ? [handledIssues.notes] : undefined);
              }}
            />
          </CardBox>
          <CardBox>
            <FormControlLabel
              control={
                <Checkbox
                  color='primary'
                  disabled={isEmailOutcome(phoneCallContext.callOutcome)}
                  checked={phoneCallContext.sendCompanyEmail}
                  onChange={(e) => handlePhoneCallContextUpdate({ sendCompanyEmail: !!e.target.checked })}
                />
              }
              label={<Typography text='Send Company Email' />}
            />
          </CardBox>
          <CardBox>
            <DebouncedTextField
              fullWidth
              label='Call Center Internal Notes'
              multiline
              required={phoneCallContext.sendEscalationEmail}
              error={{
                issuePath: handledIssues.internalNotes,
                issues: fieldIssues
              }}
              debounceOptions={{ initialValue: phoneCallContext.internalNotes }}
              onChangeHandler={(internalNotes: string) => {
                handlePhoneCallContextUpdate(
                  { internalNotes },
                  internalNotes ? [handledIssues.internalNotes] : undefined
                );
              }}
            />
          </CardBox>
          {!authRoles.isAccountUser && !authRoles.isAccountAdmin && (
            <CardBox>
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    checked={phoneCallContext.sendEscalationEmail}
                    onChange={(e) =>
                      handlePhoneCallContextUpdate(
                        { sendEscalationEmail: !!e.target.checked },
                        !e.target.checked ? [handledIssues.internalNotes] : undefined
                      )
                    }
                  />
                }
                label={<Typography text='Send Escalation Email' />}
              />
            </CardBox>
          )}
          <CardBox>
            <FormControlLabel
              control={<Checkbox color='primary' disabled checked={companyContext.switched} />}
              label={<Typography text='Company Switched' />}
            />
          </CardBox>
          {notifyTechRequired && (
            <Box>
              <Card
                variant='outlined'
                sx={{ padding: 0.75, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={<Checkbox checked={techNotified} onChange={(e) => setTechNotified(e.target.checked)} />}
                  label={<Typography text='Tech Notified' />}
                />
                <Tooltip title='View on call instructions'>
                  <IconButton color='primary' onClick={handleNotifyTechInfoClicked}>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Card>
            </Box>
          )}
          {showCompleteCallWarning && (
            <Box>
              <WarningAlert>
                <Typography
                  text={`Call Outcome (${phoneCallContext.callOutcome}) will release the requested Appointment.`}
                />
              </WarningAlert>
            </Box>
          )}
          <Box>
            <Tooltip title={notifyTechRequired && !techNotified ? 'Tech must be notified to complete the call.' : ''}>
              <span>
                <CompleteCallWrapper nonFieldErrors={nonFieldErrors}>
                  <Button
                    disabled={notifyTechRequired && !techNotified}
                    fullWidth
                    onClick={handleCompleteCall}
                    startIcon={showCompleteCallWarning ? <WarningIcon /> : <CheckIcon />}
                    color='success'
                    variant='contained'>
                    Complete Call
                  </Button>
                </CompleteCallWrapper>
              </span>
            </Tooltip>
          </Box>
        </Stack>
        <Divider />
        {showFooter && <CallDetailsFooter />}
        {techAssignDialog.element}
        {locationFormDialog.element}
        {confirmationDialog.element}
        {alertDialog.element}
      </ErrorBoundary>
    </Box>
  );
};
